import { observer } from 'mobx-react-lite'
import Link from 'next/link'
import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'

import { LinkButton } from '@components/button/link'
import type {
    ButtonTrackingProps,
    CommonButtonProps,
} from '@components/button/props'
import type { ActionLocalPathnameFragment } from '@graphql/fragments/ActionLocalPathnameFragment.graphql'
import type { WithTestId } from '@lib/utils/testid'
import { LINK } from '@shared/util/link'

/** Action button that navigates the current website. */
export const ActionLocalPathname: FunctionComponent<Props> = observer(
    ({
        title,
        'data-testid': testId,
        placement,
        containerElementId,
        path,
        preset = 'primary',
        size,
        ...restOfProps
    }) => {
        const link = LINK.cms({ path, ...restOfProps })
        const router = useRouter()

        // Preserve query params from the url if they exist
        const urlParams = new URLSearchParams(router.asPath.split('?')[1] || '')
        const queryParams = Object.fromEntries(urlParams.entries())
        const linkWithParams = link.addQueryParams(queryParams)

        return (
            <Link
                passHref
                href={linkWithParams.route}
                as={linkWithParams.serialize(router)}
                legacyBehavior
            >
                <LinkButton
                    preset={preset || 'primary'}
                    size={size ?? 'medium'}
                    data-testid={testId}
                    placement={placement}
                    disabled={!path}
                >
                    {title}
                </LinkButton>
            </Link>
        )
    },
)

type Props = WithTestId<Omit<ActionLocalPathnameFragment, 'type' | 'sys'>> &
    ButtonTrackingProps & {
        preset?: string
        size?: CommonButtonProps['size']
    }
